import { SuppliesShippingConditionStep } from "@redotech/redo-flows-common/advanced-flow-db-parser";
import {
  SuppliesShippingConditionKind,
  SuppliesShippingListOperator,
  SuppliesShippingNumberOperator,
} from "@redotech/redo-flows-core-schema-common/common";
import { t } from "@redotech/redo-i18n";
import { assertNever } from "@redotech/util/type";

export function suppliesShippingOperatorLabel(
  operator: SuppliesShippingNumberOperator,
): string {
  switch (operator) {
    case SuppliesShippingNumberOperator.LESS_THAN:
      return t("is less than");
    case SuppliesShippingNumberOperator.LESS_THAN_OR_EQUAL:
      return t("is at most");
    case SuppliesShippingNumberOperator.EQUAL:
      return t("equals");
    case SuppliesShippingNumberOperator.GREATER_THAN:
      return t("is more than");
    case SuppliesShippingNumberOperator.GREATER_THAN_OR_EQUAL:
      return t("is at least");
    case SuppliesShippingNumberOperator.BETWEEN:
      return t("is between");
    default:
      return assertNever(operator);
  }
}

export function suppliesShippingListOperatorLabel(
  operator: SuppliesShippingListOperator,
): string {
  return operator === SuppliesShippingListOperator.NONE_OF
    ? t("none of")
    : t("any of");
}

function cartValueSummary(
  operator: Exclude<
    SuppliesShippingNumberOperator,
    SuppliesShippingNumberOperator.BETWEEN
  >,
  amount: string,
): string {
  switch (operator) {
    case SuppliesShippingNumberOperator.LESS_THAN:
      return t("Cart value is less than {{amount}}", { amount });
    case SuppliesShippingNumberOperator.LESS_THAN_OR_EQUAL:
      return t("Cart value is at most {{amount}}", { amount });
    case SuppliesShippingNumberOperator.EQUAL:
      return t("Cart value equals {{amount}}", { amount });
    case SuppliesShippingNumberOperator.GREATER_THAN:
      return t("Cart value is more than {{amount}}", { amount });
    case SuppliesShippingNumberOperator.GREATER_THAN_OR_EQUAL:
      return t("Cart value is at least {{amount}}", { amount });
    default:
      return assertNever(operator);
  }
}

/** Human summary of a condition, for the node body and inspector header. */
export function suppliesShippingConditionSummary(
  condition: SuppliesShippingConditionStep["condition"] | undefined,
): string | null {
  if (!condition) {
    return null;
  }
  switch (condition.kind) {
    case SuppliesShippingConditionKind.CART_VALUE: {
      const dollars = (condition.amountMinorUnits / 100).toFixed(2);
      if (condition.operator === SuppliesShippingNumberOperator.BETWEEN) {
        const max = condition.maxAmountMinorUnits;
        const maxDollars = max == null ? "?" : (max / 100).toFixed(2);
        return t("Cart value is between {{minAmount}} and {{maxAmount}}", {
          minAmount: `$${dollars}`,
          maxAmount: `$${maxDollars}`,
        });
      }
      return cartValueSummary(condition.operator, `$${dollars}`);
    }
    case SuppliesShippingConditionKind.FIRST_TIME_CUSTOMER:
      return t("First supplies order");
    case SuppliesShippingConditionKind.AUTOMATED_REORDER:
      return t("Automated reorder");
    case SuppliesShippingConditionKind.MERCHANT: {
      const count = condition.accountIds.length;
      return condition.operator === SuppliesShippingListOperator.NONE_OF
        ? t("None of {{count}} merchants", { count })
        : t("Any of {{count}} merchants", { count });
    }
    case SuppliesShippingConditionKind.CART_ITEMS: {
      const count = condition.productIds.length;
      return condition.operator === SuppliesShippingListOperator.NONE_OF
        ? t("Cart has none of {{count}} products", { count })
        : t("Cart has any of {{count}} products", { count });
    }
    case SuppliesShippingConditionKind.PRODUCT_FAMILY_TAGS: {
      const tags = condition.tags.join(", ");
      return condition.operator === SuppliesShippingListOperator.NONE_OF
        ? t("Family tagged none of {{tags}}", { tags })
        : t("Family tagged any of {{tags}}", { tags });
    }
    case SuppliesShippingConditionKind.DESTINATION_PROVINCE: {
      const provinces = condition.provinces.join(", ");
      return condition.operator === SuppliesShippingListOperator.NONE_OF
        ? t("Ships to none of {{provinces}}", { provinces })
        : t("Ships to any of {{provinces}}", { provinces });
    }
    default:
      return assertNever(condition);
  }
}
